import { RebillyConfigType } from '@xyo-network/api-checkout-apollo/bindings/src/utils/rebilly'

import { getRequestContext } from '../../context'
import { waitFor } from '../../utils'
import { Boot } from './types'

export const rebillyType: RebillyConfigType = (process.env.REACT_APP_REBILLY_TYPE as RebillyConfigType) || 'live'
const apiUrl = getApiUrl(rebillyType)

const bootsCache = new Map<string, Promise<BootsResult>>()

function getApiUrl(type: RebillyConfigType) {
  switch (type) {
    case 'sandbox':
    case 'dev':
      return 'https://prod.coinapp.co/boots-local'
    case 'local':
      return 'http://localhost:8001'
    default:
      return 'https://prod.coinapp.co/boots'
  }
}

export interface BootsResult {
  bootEquipped: string
  records: Boot[]
  mineCount: number
}

export interface BootSyncResult {
  claimed: Boot[]
}

export function clearBootCache() {
  bootsCache.clear()
}

export function rmBootCache(key: string) {
  bootsCache.delete(key)
}

export async function fetchBoot(uniqueId: string): Promise<Boot> {
  const res = await fetch(`${apiUrl}/v1/boot/${uniqueId}`)

  const data = await res.json()

  return data.claim
}

export async function fetchBoots(): Promise<BootsResult> {
  const { headers } = await getRequestContext()

  const res = await fetch(`${apiUrl}/v1/user/list`, {
    headers,
  })

  const data = await res.json()

  return data
}

export function fetchBootsFromCache() {
  const cached = bootsCache.get('list')
  if (cached) return cached

  const request = fetchBoots()
  bootsCache.set('list', request)

  return request
}

export async function syncBoots(address?: string): Promise<BootSyncResult> {
  const { headers } = await getRequestContext()

  const res = await fetch(`${apiUrl}/v1/boot/sync`, {
    body: JSON.stringify({ address }),
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })

  const data = res.json()
  return data
}

export async function equipBoot(uniqueId: string): Promise<{ success: boolean }> {
  const { headers } = await getRequestContext()

  const res = await fetch(`${apiUrl}/v1/user/equip/${uniqueId}`, {
    headers,
    method: 'POST',
  })

  const data = await res.json()
  if (data.message) {
    throw new Error(data.message)
  }
  return data
}

export async function unequipBoot(uniqueId: string): Promise<{ success: boolean }> {
  const { headers } = await getRequestContext()

  const res = await fetch(`${apiUrl}/v1/user/unequip/${uniqueId}`, {
    headers,
    method: 'POST',
  })

  const data = await res.json()
  if (data.message) {
    throw new Error(data.message)
  }
  return data
}

export interface ClaimParams {
  receipt: string
  uid: string
  email: string
  ethAddress: string
}

export async function claimBoots(params: ClaimParams): Promise<Boot[]> {
  if (params.receipt === 'test') {
    await waitFor(1000)
    return defaultClaims
  }

  const res = await fetch(`${apiUrl}/v1/claim/rebilly`, {
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  })
  const data = await res.json()
  if (data.message) {
    throw new Error(data.message)
  }

  rmBootCache('list')
  return data.claims
}

export interface CanClaimRandomBootResult {
  totalReceipts: number
  dailyReceipts: number
  error: string
}

export async function canClaimRandomBoot(): Promise<CanClaimRandomBootResult> {
  const { headers } = await getRequestContext()
  const res = await fetch(`${apiUrl}/v1/receipt/canclaim`, { headers })
  const data = await res.json()
  if (data.message) {
    throw new Error(data.message)
  }
  return data
}

const defaultClaims: Boot[] = [
  {
    claimedTime: '2023-05-26T16:15:33.929Z',
    claimedUid: 'pw5d7f51zJVfWWErKsYHLotpIL92',
    ethAddress: '',
    forged: '',
    globalWeb: 'https://boots.coinapp.co/images/40000000000000/globalWeb',
    image: 'https://boots.coinapp.co/images/40000000000000/500x500',
    localWeb: 'https://boots.coinapp.co/images/40000000000000/localWeb',
    merged: '',
    mergedUid: '',
    mintState: 'minted',
    minted: true,
    thumb: 'https://boots.coinapp.co/images/40000000000000/100x100',
    tier: '4',
    tierName: 'Epic',
    tokenURI: '',
    uniqueId: '40000000000000',
  },
  {
    claimedTime: '2023-05-26T16:15:33.929Z',
    claimedUid: 'pw5d7f51zJVfWWErKsYHLotpIL92',
    ethAddress: '',
    forged: '',
    globalWeb: 'https://boots.coinapp.co/images/20000000000000/globalWeb',
    image: 'https://boots.coinapp.co/images/20000000000000/500x500',
    localWeb: 'https://boots.coinapp.co/images/20000000000000/localWeb',
    merged: '',
    mergedUid: '',
    mintState: 'minted',
    minted: true,
    thumb: 'https://boots.coinapp.co/images/20000000000000/100x100',
    tier: '2',
    tierName: 'Uncommon',
    tokenURI: '',
    uniqueId: '20000000000000',
  },
]
